import React from 'react';
import { Calendar, Trophy, Target, Users } from 'lucide-react';
import { StatsDisplay } from './StatsDisplay';
import type { DailyChallengeViewProps } from './types';
import styles from './DailyChallengeView.module.css';

export function DailyChallengeView({
  stats,
  challenge,
  frequency,
  scoreDescriptor,
  signatureColor,
  isCurrentPeriod = true,
}: DailyChallengeViewProps) {
  // Format the challenge title based on frequency and period
  const getChallengeTitle = () => {
    if (!isCurrentPeriod) {
      if (challenge.displayLabel) {
        // Home page stream: "Yesterday's Puzzle", "Monday's Puzzle"
        return `${challenge.displayLabel}'s Puzzle`;
      }
      // Archive: "Puzzle #58"
      return `Puzzle #${challenge.puzzleNumber}`;
    }
    const prefix = frequency === 'daily' ? "Today's" : "This Week's";
    return `${prefix} Puzzle`;
  };
  
  // Format average score based on score descriptor
  const formatAverageScore = (score?: number) => {
    if (score === undefined || score === null) return 'N/A';
    return score.toFixed(1);
  };
  
  // Format success rate
  const formatSuccessRate = (rate?: number) => {
    if (rate === undefined || rate === null) return 'N/A';
    return `${rate.toFixed(0)}%`;
  };
  
  // Format current streak
  const formatStreak = (streak?: number) => {
    if (streak === undefined || streak === null) return '0';
    return streak.toString();
  };
  
  // Get streak label based on frequency
  const getStreakLabel = () => {
    return frequency === 'daily' ? 'Current Streak' : 'Week Streak';
  };
  
  // Get average label text
  const getAverageLabel = () => {
    if (scoreDescriptor.toLowerCase().includes('degrees')) {
      return 'Avg Degrees';
    } else if (scoreDescriptor.toLowerCase().includes('attempts')) {
      return 'Avg Attempts';
    }
    return 'Average';
  };
  
  // Format community average text
  const formatCommunityAverage = (score?: number) => {
    if (score === undefined || score === null) return '';
    
    if (scoreDescriptor.toLowerCase().includes('degrees')) {
      return `Average solve: ${score.toFixed(1)} degrees`;
    } else if (scoreDescriptor.toLowerCase().includes('attempts')) {
      return `Average solve: ${score.toFixed(1)} attempts`;
    } else {
      return `Average solve: ${score.toFixed(1)}`;
    }
  };
  
  return (
    <div className={styles.container}>
      {/* Player Stats Grid */}
      <div className={styles.statsGrid}>
        <StatsDisplay
          value={formatStreak(stats?.currentStreak)}
          label={getStreakLabel()}
          signatureColor={signatureColor}
        />
        <StatsDisplay
          value={formatAverageScore(stats?.averageScore)}
          label={getAverageLabel()}
          signatureColor={signatureColor}
        />
        <StatsDisplay
          value={formatSuccessRate(stats?.successRate)}
          label="Success Rate"
          signatureColor={signatureColor}
        />
      </div>
      
      {/* Challenge Info Section */}
      <div className={styles.challengeInfo} style={{ backgroundColor: 'var(--signature-color-soft)' }}>
        <h3 className={styles.challengeLabel}>
          {getChallengeTitle()}
        </h3>
        <div className={styles.puzzleTitle}>
          <div
            className={styles.puzzleName}
            style={{ color: signatureColor, WebkitTextFillColor: signatureColor }}
          >
            &#x201C;{challenge.puzzleName}&#x201D;
          </div>
        </div>
        {stats?.averageSolve !== undefined && (
          <p className={styles.averageSolve} style={{ color: signatureColor, WebkitTextFillColor: signatureColor }}>
            {formatCommunityAverage(stats?.averageSolve)}
          </p>
        )}
      </div>
    </div>
  );
}